import React, { useState } from 'react';
import { CalendarClock, Lock, Unlock } from 'lucide-react';
import '../../shared-modules.css';
import '../forms/FinanceForms.css';
import '../../FixedAssetsView/FixedAssetsView.css';
import '../../PurchasesView/PurchasesView.css';
import '../../../../../components/shared/DataTable/DataTable.css';

// Clase de cuenta como en SAP: + todas, A activos fijos, D deudores, K acreedores, M materiales, S mayor
type AccountClass = '+' | 'A' | 'D' | 'K' | 'M' | 'S';

interface PeriodRow {
  id: string;
  accountClass: AccountClass;
  accountFrom: string;
  accountTo: string;
}

// Intervalo 1 = periodos normales; intervalo 2 = periodos especiales de cierre (13-16)
interface PeriodWindow {
  accountFrom: string;
  accountTo: string;
  p1From: string;
  p1FromYear: string;
  p1To: string;
  p1ToYear: string;
  p2From: string;
  p2FromYear: string;
  p2To: string;
  p2ToYear: string;
  authGroup: string;
}

type WindowField = keyof PeriodWindow;

// Hasta la última cuenta, como en SAP
const LAST_ACCOUNT = 'ZZZZZZZZZZ';

// Filas por página, las mismas que DataTable (Usuarios, Libros, Roles…)
const PAGE_SIZE = 50;

// Filas de la captura; el rango de cuenta se puede cambiar
const PERIOD_ROWS: PeriodRow[] = [
  { id: 'all', accountClass: '+', accountFrom: '', accountTo: '' },
  { id: 'A', accountClass: 'A', accountFrom: '', accountTo: LAST_ACCOUNT },
  { id: 'D', accountClass: 'D', accountFrom: '', accountTo: LAST_ACCOUNT },
  { id: 'K', accountClass: 'K', accountFrom: '', accountTo: LAST_ACCOUNT },
  { id: 'M', accountClass: 'M', accountFrom: '', accountTo: LAST_ACCOUNT },
  { id: 'S-472', accountClass: 'S', accountFrom: '472000', accountTo: '472999' },
  { id: 'S-477', accountClass: 'S', accountFrom: '477000', accountTo: '477999' },
  { id: 'S', accountClass: 'S', accountFrom: '', accountTo: LAST_ACCOUNT }
];

// Sin endpoint todavía: se entra con las filas de partida, periodos vacíos y todo abierto
const emptyWindows = (): Record<string, PeriodWindow> =>
  PERIOD_ROWS.reduce((acc, row) => {
    acc[row.id] = {
      accountFrom: row.accountFrom, accountTo: row.accountTo,
      p1From: '', p1FromYear: '', p1To: '', p1ToYear: '',
      p2From: '', p2FromYear: '', p2To: '', p2ToYear: '',
      authGroup: ''
    };
    return acc;
  }, {} as Record<string, PeriodWindow>);

interface FinancePostingPeriodsViewProps {
  // Periodo elegido en la pantalla de cierre: los candados son de ese periodo
  period: string;
}

export const FinancePostingPeriodsView = ({ period }: FinancePostingPeriodsViewProps) => {
  const [windows, setWindows] = useState<Record<string, PeriodWindow>>(emptyWindows);
  // Candado por periodo y fila, con clave "Enero 2026|D"
  const [locks, setLocks] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const pageRows = PERIOD_ROWS.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const lockKey = (rowId: string) => `${period}|${rowId}`;
  const isLocked = (rowId: string) => !!locks[lockKey(rowId)];

  const updateWindow = (rowId: string, field: WindowField, value: string) =>
    setWindows(prev => ({ ...prev, [rowId]: { ...prev[rowId], [field]: value } }));

  const toggleLock = (rowId: string) =>
    setLocks(prev => ({ ...prev, [lockKey(rowId)]: !prev[lockKey(rowId)] }));

  const lockedCount = PERIOD_ROWS.filter(r => isLocked(r.id)).length;

  // Mismos campos que las líneas del pedido de compra
  const renderInput = (rowId: string, field: WindowField) => (
    <input
      type="text"
      value={windows[rowId][field]}
      disabled={isLocked(rowId)}
      onChange={(e) => updateWindow(rowId, field, e.target.value)}
      className="module-form-input"
    />
  );

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">CIERRE • {period.toUpperCase()}</span>
          <h3 className="fin-doc__title"><CalendarClock size={14} /> Periodos contables</h3>
        </div>
        <span className="fin-doc__badge">{lockedCount} / {PERIOD_ROWS.length} BLOQUEADAS</span>
      </div>

      <div className="purchases-view__lines-wrapper">
        <table className="purchases-view__lines-table fin-period-table">
          <thead>
            <tr>
              <th title="Clase de cuenta">C</th>
              <th>DE CUENTA</th>
              <th>A CUENTA</th>
              <th title="Desde periodo (intervalo 1)">DE PER.1</th>
              <th title="Ejercicio">EJ.</th>
              <th title="Hasta periodo (intervalo 1)">A PER.1</th>
              <th title="Ejercicio">EJ.</th>
              <th title="Desde periodo (intervalo 2: periodos especiales 13-16)">PER.DE 2</th>
              <th title="Ejercicio">EJ.</th>
              <th title="Hasta periodo (intervalo 2)">A PER.2</th>
              <th title="Ejercicio">EJ.</th>
              <th title="Grupo de autorización">GRA</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pageRows.map(row => {
              const locked = isLocked(row.id);
              return (
                <tr key={row.id} className={locked ? 'fin-period-row--locked' : undefined}>
                  <td>
                    <span className={`fixed-assets-view__code ${row.accountClass === '+' ? 'fin-period-class--all' : ''}`}>{row.accountClass}</span>
                  </td>
                  <td>{renderInput(row.id, 'accountFrom')}</td>
                  <td>{renderInput(row.id, 'accountTo')}</td>
                  <td>{renderInput(row.id, 'p1From')}</td>
                  <td>{renderInput(row.id, 'p1FromYear')}</td>
                  <td>{renderInput(row.id, 'p1To')}</td>
                  <td>{renderInput(row.id, 'p1ToYear')}</td>
                  <td>{renderInput(row.id, 'p2From')}</td>
                  <td>{renderInput(row.id, 'p2FromYear')}</td>
                  <td>{renderInput(row.id, 'p2To')}</td>
                  <td>{renderInput(row.id, 'p2ToYear')}</td>
                  <td>{renderInput(row.id, 'authGroup')}</td>
                  <td>
                    <button
                      type="button"
                      className={`fin-period-lock ${locked ? 'is-locked' : ''}`}
                      onClick={() => toggleLock(row.id)}
                      title={`${locked ? 'Desbloquear' : 'Bloquear'} ${row.accountClass} en ${period}`}
                    >
                      {locked ? <Lock size={12} /> : <Unlock size={12} />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Misma paginación que DataTable */}
      <div className="data-table-pagination">
        <div className="data-table-pagination-info">
          Mostrando <span>{pageRows.length}</span> de <span>{PERIOD_ROWS.length}</span> registros
        </div>
        <div className="data-table-pagination-controls">
          <button
            type="button"
            className="data-table-pagination-btn"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            PREV
          </button>
          <button type="button" className="data-table-pagination-btn active">{page}</button>
          <button
            type="button"
            className="data-table-pagination-btn"
            disabled={page * PAGE_SIZE >= PERIOD_ROWS.length}
            onClick={() => setPage(page + 1)}
          >
            NEXT
          </button>
        </div>
      </div>

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled title="Pendiente de endpoint">
          Guardar
        </button>
      </div>
    </div>
  );
};

export default FinancePostingPeriodsView;
